import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import packageJson from '../../../../../package.json';

const appVersion = `v${packageJson.version}-${process.env.REACT_APP_BUILD_ENV || process.env.NODE_ENV || 'dev'}`;

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' }
];

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <footer className="py-[24px] mt-[40px] border-t border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.08)]">
      <div className="flex flex-col sm:flex-row justify-between gap-[12px]">
        <div className="flex flex-col gap-[4px]">
          <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3]">
            {t('footer.company')}
          </p>
          <p className="font-body text-[12px] text-[#BFBFC0] dark:text-[#4A4A4C]">
            {t('footer.copyright')}
          </p>
        </div>
        <div className="flex gap-[16px] items-start flex-wrap">
          {/* Language selector */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-[6px] font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white transition-colors ${langOpen ? 'text-base-black dark:text-white' : ''}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              {currentLang.label}
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`transition-transform ${langOpen ? 'rotate-180' : ''}`}>
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {langOpen && (
              <div className="absolute left-0 bottom-[calc(100%+8px)] z-20 min-w-[160px] bg-white dark:bg-[#28282B] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[6px] animate-fadeInMenuUp">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-[14px] py-[8px] font-body text-[13px] text-[#111827] dark:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#333336] transition-colors ${
                      lang.code === i18n.language ? 'font-semibold' : ''
                    }`}
                  >
                    <span>{lang.label}</span>
                    {lang.code === i18n.language && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/terms"
            className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white transition-colors"
          >
            {t('footer.terms')}
          </Link>
          <Link
            to="/privacy"
            className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white transition-colors"
          >
            {t('footer.privacy')}
          </Link>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('standby_cookie_consent');
              window.location.reload();
            }}
            className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white transition-colors"
          >
            {t('footer.cookies')}
          </button>
        </div>
      </div>
      <p className="font-body text-[12px] text-[#BFBFC0] dark:text-[#4A4A4C] text-center mt-[16px]">
        {appVersion}
      </p>
    </footer>
  );
};
